import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from './client'
import type { ListResponse, Organization } from './types'

const ROOT = '/organizations'
const KEY = ['organizations'] as const

export function useOrganizations() {
  const api = useApi()
  return useQuery({
    queryKey: KEY,
    queryFn: () => api<ListResponse<Organization>>(ROOT).then((r) => r.items),
  })
}

// Organization the salaries screens default to (payroll is run through this FOP).
// Matched by name and resolved to an id at runtime so it survives re-seeded DBs;
// falls back to the first org when absent.
export const SALARY_DEFAULT_ORG_NAME = 'ФОП Ігор Король'

// Auto-pick a default org once loaded if the caller hasn't selected one yet.
// Used by screens that need a single-org context (salaries tabs, reports). When
// `preferredName` is given, that org wins over the first one if it exists.
export function useDefaultOrg(
  orgs: Organization[] | undefined,
  selectedId: string | null,
  setSelectedId: (id: string) => void,
  preferredName?: string,
) {
  useEffect(() => {
    if (selectedId != null || !orgs || orgs.length === 0) return
    const preferred = preferredName
      ? orgs.find((o) => o.name.trim().toLowerCase() === preferredName.trim().toLowerCase())
      : undefined
    setSelectedId((preferred ?? orgs[0]).id)
  }, [orgs, selectedId, setSelectedId, preferredName])
}

export type CreateOrganizationInput = {
  name: string
  legalName?: string | null
  taxId?: string | null
  b24Id?: string | null
}

export function useCreateOrganization() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateOrganizationInput) =>
      api<Organization>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export type UpdateOrganizationInput = {
  id: string
  patch: Partial<{
    name: string
    legalName: string | null
    taxId: string | null
    b24Id: string | null
  }>
}

export function useUpdateOrganization() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: UpdateOrganizationInput) =>
      api<Organization>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeleteOrganization() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
